// src/components/roles/RoleWizard/WizardStep3.tsx
import React from 'react';
import { Box, Typography, Paper, Chip, Grid } from '@mui/material';
import { getModuleIcon } from '../../../constants/module-icons';
import { RoleWizardData } from '../../../types/role.types';
import { SYSTEM_STRUCTURE, ModuleConfig, MenuConfig } from '../../../config/system-structure';
import { colors } from '../../../styles/theme/colors';

interface WizardStep3Props {
  formData: RoleWizardData;
}

const resolveActionLabel = (menu: MenuConfig | undefined, actionId: string): string => {
  if (menu == null) return actionId;

  const mainButton = menu.mainButtons?.find(
    (btn) => btn.id === actionId || btn.action === actionId,
  );
  if (mainButton) return mainButton.name;

  const rowAction = menu.rowActions?.find(
    (action) => action.id === actionId || action.action === actionId,
  );
  if (rowAction) return rowAction.name;

  const flow = menu.salesFlowActions?.find((f) => f.id === actionId);
  if (flow) return flow.name;

  const flag = menu.columnFlags?.find((f) => f.id === actionId);
  if (flag) return flag.name;

  const fieldFlag = menu.fieldFlags?.find((f) => f.id === actionId);
  if (fieldFlag) return fieldFlag.name;

  for (const group of menu.fieldFlagGroups ?? []) {
    const inGroup = group.flags.find((f) => f.id === actionId);
    if (inGroup) return inGroup.name;
  }

  for (const section of menu.fieldFlagSections ?? []) {
    for (const group of section.groups) {
      const inGroup = group.flags.find((f) => f.id === actionId);
      if (inGroup) return inGroup.name;
    }
  }

  return actionId;
};

const WizardStep3: React.FC<WizardStep3Props> = ({ formData }) => {
  const getEnabledModules = (): ModuleConfig[] => {
    return SYSTEM_STRUCTURE.filter((module) => formData.permissions[module.id]?.enabled);
  };

  const getMenuPermissions = (moduleId: string, menuId: string) => {
    const menuPerms = formData.permissions[moduleId]?.menus[menuId];
    if (!menuPerms) {
      return {
        visible: false,
        mainButtons: [] as string[],
        rowActions: [] as string[],
        salesFlowActions: [] as string[],
        columnFlags: [] as string[],
        fieldFlags: [] as string[],
      };
    }

    const mainButtons = Object.entries(menuPerms.mainButtons)
      .filter(([_, enabled]) => enabled)
      .map(([buttonId]) => buttonId);

    const rowActions = Object.entries(menuPerms.rowActions)
      .filter(([_, enabled]) => enabled)
      .map(([actionId]) => actionId);

    const salesFlowActions = Object.entries(menuPerms.salesFlowActions ?? {})
      .filter(([_, enabled]) => enabled)
      .map(([id]) => id);

    const columnFlags = Object.entries(menuPerms.columnFlags ?? {})
      .filter(([_, enabled]) => enabled)
      .map(([id]) => id);

    const fieldFlags = Object.entries(menuPerms.fieldFlags ?? {})
      .filter(([_, enabled]) => enabled)
      .map(([id]) => id);

    return {
      visible: menuPerms.visible,
      mainButtons,
      rowActions,
      salesFlowActions,
      columnFlags,
      fieldFlags,
    };
  };

  const getTotalPermissions = (): number => {
    let count = 0;
    Object.values(formData.permissions).forEach((module) => {
      Object.values(module.menus).forEach((menu) => {
        if (menu.visible) count++;
        count += Object.values(menu.mainButtons).filter((val) => val).length;
        count += Object.values(menu.rowActions).filter((val) => val).length;
        if (menu.columnFlags) {
          count += Object.values(menu.columnFlags).filter((val) => val).length;
        }
        if (menu.fieldFlags) {
          count += Object.values(menu.fieldFlags).filter((val) => val).length;
        }
        if (menu.salesFlowActions) {
          count += Object.values(menu.salesFlowActions).filter((val) => val).length;
        }
      });
    });
    return count;
  };

  const enabledModules = getEnabledModules();
  const totalPermissions = getTotalPermissions();

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Información Básica */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Información Básica
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Nombre del Rol
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {formData.name || '—'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Descripción
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formData.description || '—'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Permisos Asignados */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Permisos Asignados
            </Typography>
            <Chip
              label={`${totalPermissions} permisos`}
              size="small"
              sx={{
                backgroundColor: '#10b981',
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Box>

          {enabledModules.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}
            >
              No se han asignado permisos
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {enabledModules.map((module) => {
                const modulePerms = formData.permissions[module.id];
                const enabledMenus = module.menus.filter((menu) => {
                  const mp = modulePerms?.menus[menu.id];
                  if (!mp) return false;
                  if (menu.configOnly) {
                    return Object.values(mp.fieldFlags ?? {}).some(Boolean);
                  }
                  return mp.visible;
                });

                if (enabledMenus.length === 0) return null;

                return (
                  <Box
                    key={module.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: 'rgba(16, 185, 129, 0.05)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: '#10b981',
                          color: 'white',
                        }}
                      >
                        {getModuleIcon(module.id)}
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {module.name.toUpperCase()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 4 }}>
                      {enabledMenus.map((menu) => {
                        const perms = getMenuPermissions(module.id, menu.id);
                        const hasPermissions =
                          perms.mainButtons.length > 0 ||
                          perms.rowActions.length > 0 ||
                          perms.salesFlowActions.length > 0 ||
                          perms.columnFlags.length > 0 ||
                          perms.fieldFlags.length > 0;

                        return (
                          <Box key={menu.id}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                              {menu.name}
                            </Typography>
                            {hasPermissions && (
                              <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {perms.mainButtons.length > 0 && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                                    >
                                      Botones:
                                    </Typography>
                                    <Box
                                      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}
                                    >
                                      {perms.mainButtons.map((buttonId) => (
                                        <Chip
                                          key={buttonId}
                                          label={resolveActionLabel(menu, buttonId)}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            backgroundColor: colors.info.main,
                                            color: 'white',
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {perms.rowActions.length > 0 && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                                    >
                                      Acciones:
                                    </Typography>
                                    <Box
                                      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}
                                    >
                                      {perms.rowActions.map((actionId) => (
                                        <Chip
                                          key={actionId}
                                          label={resolveActionLabel(menu, actionId)}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            backgroundColor: colors.secondary.main,
                                            color: 'white',
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {perms.salesFlowActions.length > 0 && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                                    >
                                      {menu.flowSectionTitle ?? 'Flujos'}:
                                    </Typography>
                                    <Box
                                      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}
                                    >
                                      {perms.salesFlowActions.map((flowId) => (
                                        <Chip
                                          key={flowId}
                                          label={resolveActionLabel(menu, flowId)}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            backgroundColor: colors.primary.main,
                                            color: 'white',
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {perms.fieldFlags.length > 0 && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                                    >
                                      Campos:
                                    </Typography>
                                    <Box
                                      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}
                                    >
                                      {perms.fieldFlags.map((flagId) => (
                                        <Chip
                                          key={flagId}
                                          label={resolveActionLabel(menu, flagId)}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            backgroundColor: colors.info.main,
                                            color: 'white',
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {perms.columnFlags.length > 0 && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                                    >
                                      Columnas / KPIs:
                                    </Typography>
                                    <Box
                                      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}
                                    >
                                      {perms.columnFlags.map((flagId) => (
                                        <Chip
                                          key={flagId}
                                          label={resolveActionLabel(menu, flagId)}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            backgroundColor: colors.warning.main,
                                            color: 'white',
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default WizardStep3;
